import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, Loader2, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initials = (name) =>
  String(name || '?')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '?';

const lastTripLabel = (c) => {
  const dests = c.destinationsCovered || [];
  if (dests.length) return dests[dests.length - 1];
  if (c.lastTravelDate) return new Date(c.lastTravelDate).getFullYear();
  return 'No trips yet';
};

/**
 * Search-first traveller picker. Queries existing travellers by name/phone/UCC
 * and only surfaces a "Create new" option when the search returns nothing —
 * so operators reuse profiles instead of spawning duplicates.
 */
const TravellerSearchSelect = ({ onSelect, allowCreate = true, placeholder = 'Search by name, phone or ID…', autoFocus = false }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/clients?search=${encodeURIComponent(term)}`, { withCredentials: true });
        setResults((Array.isArray(res.data) ? res.data : []).slice(0, 8));
        setOpen(true);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pick = (c) => {
    onSelect(c, q.trim());
    setQ('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.trim() && setOpen(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          data-testid="traveller-search-input"
          className="w-full h-11 pl-10 pr-9 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />}
      </div>

      {open && q.trim() && (
        <div
          data-testid="traveller-search-results"
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto"
        >
          {results.length > 0 ? (
            results.map((c) => (
              <button
                key={c.ucc}
                type="button"
                onClick={() => pick(c)}
                data-testid={`traveller-opt-${c.ucc}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
              >
                <span className="h-8 w-8 shrink-0 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center">
                  {initials(c.fullName || c.name)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-800 truncate">{c.fullName || c.name || '—'}</span>
                  <span className="block text-[11px] text-slate-400 truncate">
                    <span className="font-mono">{c.displayUCC || c.ucc}</span> · {lastTripLabel(c)}
                  </span>
                </span>
              </button>
            ))
          ) : !loading ? (
            allowCreate ? (
              <button
                type="button"
                onClick={() => pick(null)}
                data-testid="create-new-traveller-opt"
                className="w-full flex items-center gap-2 px-3 py-3 hover:bg-teal-50 text-teal-700 text-sm font-semibold transition-colors"
              >
                <UserPlus className="h-4 w-4" /> Create new “{q.trim()}”
              </button>
            ) : (
              <div className="px-3 py-3 text-sm text-slate-400" data-testid="traveller-search-empty">No travellers found</div>
            )
          ) : null}
        </div>
      )}
    </div>
  );
};

export default TravellerSearchSelect;
